import React, { useEffect, useMemo } from "react";
import {
  AtSign,
  Bot,
  ChartArea,
  ChevronsUpDownIcon,
  Cpu,
  EyeIcon,
  PenLine,
  Radar,
  Search,
  Sparkles,
  SquareChevronRight,
  Swords,
  TrendingUp,
  Users,
} from "lucide-react";
import Image from "next/image";
// Import other dashboard content components as needed
import { Claude, DeepSeek, Gemini, Grok, OpenAI, Perplexity } from "@lobehub/icons";
import { cn } from "@promptwatch/ui/lib/utils";
import { MonitoringChart } from "@/components/sections/features-bento";
import { motion, AnimatePresence } from "motion/react";

// Interface for the dashboard content props
interface DashboardContentProps {
  page: string;
}

// DonutChart component that renders a small donut chart
interface DonutChartProps {
  percentage: number;
  trend: "positive" | "negative" | "flat";
  size?: number;
}

export const DonutChart: React.FC<DonutChartProps> = ({ percentage, trend, size = 16 }) => {
  // Determine color based on trend
  const getColor = () => {
    switch (trend) {
      case "positive":
        return "#10b981"; // emerald-500
      case "negative":
        return "#ef4444"; // red-500
      case "flat":
        return "#6b7280"; // gray-500
      default:
        return "#6b7280";
    }
  };

  const strokeWidth = 3;
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: "stroke-dashoffset 0.5s ease-in-out" }}
        />
      </svg>
    </div>
  );
};

const sidebarData = [
  {
    title: "Monitors",
    icon: Radar,
    id: "monitors",
  },
  {
    title: "Prompts",
    icon: SquareChevronRight,
    id: "prompts",
  },
  // {
  //   title: "AI Overviews",
  //   icon: Sparkles,
  //   id: "overviews",
  // },
  {
    title: "Citations",
    icon: PenLine,
    id: "citations",
  },
  {
    title: "Analytics",
    icon: ChartArea,
    id: "analytics",
  },
];

// Project selector component (mockup - non-functional)
const ProjectSelector = () => {
  return (
    <div className="relative">
      <div
        className={cn(
          "w-full flex items-center justify-between px-1 py-1 text-xs rounded-md text-gray-700 bg-white border border-gray-200",
        )}
      >
        <div className="flex items-center space-x-2">
          <Image src="/booking.jpeg" alt="Hubspot logo" width={20} height={20} className="rounded-sm border border-gray-200" />
          <span className="font-medium">Booking.com</span>
        </div>
        <ChevronsUpDownIcon className="h-3 w-3" />
      </div>
    </div>
  );
};
// Main dashboard component
export function MockupDashboard({ page }: { page?: string }) {
  // Get a random page to start with
  const getRandomPage = () => {
    const randomIndex = Math.floor(Math.random() * sidebarData.length);
    return sidebarData[randomIndex].id;
  };

  // Using the data structure from app-sidebar.tsx - keeping original navigation
  const [activePage, setActivePage] = React.useState<string | undefined>(undefined);
  const [autoRotate, setAutoRotate] = React.useState(true);

  // Initialize state after mount to avoid hydration mismatch
  useEffect(() => {
    setActivePage(page || getRandomPage());
  }, [page]);

  const handlePageChange = (pageId: string) => {
    setActivePage(pageId);
    setAutoRotate(false);
  };

  useEffect(() => {
    if (!autoRotate || !activePage) return;

    const rotationInterval = setInterval(() => {
      const currentIndex = sidebarData.findIndex((item) => item.id === activePage);
      const nextIndex = (currentIndex + 1) % sidebarData.length;
      setActivePage(sidebarData[nextIndex].id);
    }, 4000); // Rotate every 4 seconds

    return () => clearInterval(rotationInterval);
  }, [activePage, autoRotate]);

  return (
    <div className="flex flex-col md:flex-row bg-white">
      {/* Left Sidebar */}
      <div className="hidden md:flex md:w-48 bg-gray-50/60 p-3 flex-col border-r">
        <div className="mb-4">
          <p className="space-y-2 text-sm font-medium text-gray-800 px-1 pt-2 ">
            <Image src="/logo.svg" alt="Promptwatch logo" width={120} height={100} />
          </p>
        </div>

        <nav className="space-y-4 flex-1">
          <ProjectSelector />
          <div className="space-y-2">
            {sidebarData.map((item) => (
              <button
                key={item.title}
                type="button"
                className={cn(
                  "flex items-center px-3 py-2 text-xs rounded-md text-gray-700 bg-gray-100/60 hover:bg-gray-100 cursor-pointer border-none w-full text-left",
                  activePage === item.id && "bg-gray-100 font-medium",
                )}
                onClick={() => handlePageChange(item.id)}
              >
                <item.icon className="mr-2 h-4 w-4" />
                {item.title}
                {item.id === "overviews" && (
                  <div className="ml-auto">
                    <span className="px-1.5 py-0.5 text-[9px] font-medium bg-emerald-100 text-emerald-700 rounded-full">New</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </nav>
        <nav>
          <div className={cn("flex items-center px-3 py-2 text-xs rounded-md text-gray-700")}>
            <Users className="mr-2 h-4 w-4" />
            Team members
          </div>
          {/* <div className={cn("flex items-center px-3 py-2 text-xs rounded-md text-gray-700")}>
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </div> */}
        </nav>
      </div>

      {/* Right Dashboard Content with AnimatePresence for fade effect */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activePage}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex-1 "
        >
          <div className="h-[540px] lg:h-[500px] overflow-hidden">
            <DashboardContent page={activePage || "monitors"} />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// Component to render different dashboard contents based on page prop
function DashboardContent({ page }: DashboardContentProps) {
  switch (page) {
    case "analytics":
      return <AnalyticsDashboard />;
    case "monitors":
      return <BrandMonitoring />;
    case "prompts":
      return <PromptMonitoring />;
    case "overviews":
      return <AIOverviewSection />;
    case "citations":
      return <CitationSection />;
    // Add more cases for other pages
  }
}

function AnalyticsDashboard() {
  const data = [
    { month: "Jan", chatgpt: 872, claude: 1289, gemini: 95, perplexity: 1187 },
    { month: "Feb", chatgpt: 943, claude: 1423, gemini: 178, perplexity: 1462 },
    {
      month: "Mar",
      chatgpt: 1324,
      claude: 2076,
      gemini: 1753,
      perplexity: 1342,
    },
    {
      month: "Apr",
      chatgpt: 2691,
      claude: 3287,
      gemini: 3542,
      perplexity: 3378,
    },
    {
      month: "May",
      chatgpt: 3618,
      claude: 2793,
      gemini: 2647,
      perplexity: 3756,
    },
    {
      month: "Jun",
      chatgpt: 4157,
      claude: 5392,
      gemini: 4031,
      perplexity: 4583,
    },
    {
      month: "Jul",
      chatgpt: 4023,
      claude: 4967,
      gemini: 4078,
      perplexity: 4271,
    },
    {
      month: "Aug",
      chatgpt: 4632,
      claude: 5187,
      gemini: 4821,
      perplexity: 5042,
    },
    {
      month: "Sep",
      chatgpt: 5243,
      claude: 5698,
      gemini: 5276,
      perplexity: 5731,
    },
    {
      month: "Oct",
      chatgpt: 7764,
      claude: 8312,
      gemini: 7893,
      perplexity: 8057,
    },
    {
      month: "Nov",
      chatgpt: 9527,
      claude: 9812,
      gemini: 9384,
      perplexity: 9653,
    },
    {
      month: "Dec",
      chatgpt: 12143,
      claude: 12487,
      gemini: 11276,
      perplexity: 12298,
    },
  ];

  return (
    <div className="flex-1 p-6 bg-white">
      <div className="mb-2">
        <div className="flex items-center gap-2">
          <Image src="/miro.jpeg" alt="Miro logo" width={25} height={25} className="rounded-md border border-gray-200" />
          <p className="text-lg font-semibold text-gray-800">Miro AI Traffic Analytics</p>
          <div className="hidden md:flex items-center gap-2 ml-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
            <span className="text-xs text-gray-600">89 active visitors</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Monitor traffic from LLM models</p>
      </div>

      <div className="flex gap-1 mb-2 justify-end">
        <div className="hidden sm:flex bg-gray-50 px-2 py-1 rounded-sm border border-gray-100 flex items-center">
          <span className="text-xs font-medium text-gray-700">Views</span>
          <span className="text-xs font-bold text-gray-800 ml-1">1.2M</span>
        </div>
        <div className="hidden sm:flex bg-gray-50 px-2 py-1 rounded-sm border border-gray-100 items-center">
          <span className="text-xs font-medium text-gray-700">Bounce</span>
          <span className="text-xs font-bold text-emerald-600 ml-1">32%</span>
        </div>
        <div className="hidden sm:flex bg-gray-50 px-2 py-1 rounded-sm border border-gray-100 items-center">
          <span className="text-xs font-medium text-gray-700">Conv</span>
          <span className="text-xs font-bold text-emerald-600 ml-1">4.8%</span>
        </div>
        <div className="hidden sm:flex bg-gray-50 px-2 py-1 rounded-sm border border-gray-100 items-center">
          <span className="text-xs font-medium text-gray-700">Duration</span>
          <span className="text-xs font-bold text-gray-800 ml-1">3m 42s</span>
        </div>
      </div>

      <div className="mb-3">
        {/* <p className="text-sm font-medium text-gray-700 mb-3">Model Usage Trends</p> */}
        <div className="border border-gray-200 rounded-lg  bg-white">
          <MonitoringChart
            data={data}
            showGrid
            // config={llmChartConfig}
            height={220}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
          <p className="text-xs font-medium text-gray-700">Total Queries</p>
          <p className="text-base font-bold text-blue-600 mt-1">24,731</p>
          <p className="text-[10px] text-gray-500 mt-0.5">+18% from last month</p>
        </div>
        <div className="hidden md:block bg-gray-50 p-3 rounded-lg border border-gray-100">
          <p className="text-xs font-medium text-gray-700">Website Visitors</p>
          <p className="text-base font-bold text-gray-800 mt-1">298,842</p>
          <p className="text-[10px] text-gray-500 mt-0.5">+12% from last month</p>
        </div>
        <div className="hidden md:block bg-gray-50 p-3 rounded-lg border border-gray-100">
          <p className="text-xs font-medium text-gray-700">Avg. Time on Site</p>
          <p className="text-base font-bold text-emerald-600 mt-1">56.8s</p>
          <p className="text-[10px] text-gray-500 mt-0.5">+0.9s from last month</p>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
          <p className="text-xs font-medium text-gray-700">Top LLM</p>
          <div className="flex items-center gap-2 mt-1">
            <OpenAI size={15} type="color" />
            <p className="text-base font-bold text-gray-800">ChatGPT</p>
          </div>
          <p className="hidden sm:block text-[10px] text-gray-500 mt-0.5">58% market share</p>
        </div>
      </div>
    </div>
  );
}

function BrandMonitoring() {
  // Pre-generate model data with percentage data in a single memo
  const modelData = useMemo(() => {
    return [
      {
        rank: 1,
        model: "ChatGPT",
        modelVersion: "GPT-4o",
        icon: OpenAI,
        score: "1,247",
        topCompetitorName: "Expedia",
        topCompetitorIcon: "/expedia.jpeg",
        reasoning: "94%",
        knowledge: "98%",
        cost: "↑ 12%",
        percentage: 94,
        trend: "positive",
      },
      {
        rank: 2,
        model: "Claude",
        modelVersion: "3.7 Sonnet",
        icon: Claude,
        score: "983",

        topCompetitorName: "Kayak",
        topCompetitorIcon: "/kayak.jpeg",

        reasoning: "88%",
        knowledge: "93%",
        cost: "↑ 5%",
        percentage: 88,
        trend: "positive",
      },
      {
        rank: 3,
        model: "Deepseek",
        modelVersion: "V3",
        icon: DeepSeek,
        score: "689",
        reasoning: "81%",

        topCompetitorName: "Hotels.com",
        topCompetitorIcon: "/hotels.jpeg",

        knowledge: "87%",
        cost: "↑ 2%",
        percentage: 81,
        trend: "positive",
      },
      {
        rank: 4,
        model: "Gemini",
        modelVersion: "Gemini Pro 2.5",
        icon: Gemini,
        score: "542",

        topCompetitorName: "Agoda",
        topCompetitorIcon: "/agoda.jpeg",

        reasoning: "76%",
        knowledge: "79%",
        cost: "↓ 1%",
        percentage: 76,
        trend: "flat",
      },
      {
        rank: 5,
        model: "Perplexity",
        modelVersion: "Sonar Reasoning",
        icon: Perplexity,
        score: "423",
        reasoning: "85%",

        topCompetitorName: "Kayak",
        topCompetitorIcon: "/kayak.jpeg",

        knowledge: "89%",
        cost: "↓ 2%",
        percentage: 68,
        trend: "negative",
      },
    ];
  }, []);

  return (
    <div className="flex-1 p-6 bg-white">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Image src="/booking.jpeg" alt="Expedia logo" width={25} height={25} className="rounded-md border border-gray-200" />
          <p className="text-lg font-semibold text-gray-800">Booking LA Monitoring</p>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Monitor how LLMs mention your brand compared to competitors</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
          <p className="text-xs font-medium text-gray-700">Your Brand Mentions</p>
          <p className="text-base font-bold text-blue-600 mt-1">1,247</p>
          <p className="text-[10px] text-gray-500 mt-0.5">+12% from last month</p>
        </div>
        <div className="hidden md:block bg-gray-50 p-3 rounded-lg border border-gray-100">
          <p className="text-xs font-medium text-gray-700">Esitmated Traffic</p>
          <p className="text-base font-bold text-gray-800 mt-1">8,000 - 10,000</p>
          <p className="text-[10px] text-gray-500 mt-0.5">Last updated today</p>
        </div>
        <div className="hidden md:block bg-gray-50 p-3 rounded-lg border border-gray-100">
          <p className="text-xs font-medium text-gray-700">Visibility Score</p>
          <p className="text-base font-bold text-emerald-500 mt-1">87%</p>
          <p className="text-[10px] text-gray-500 mt-0.5">Positive brand perception</p>
        </div>
      </div>
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          {/* <Search className="size-4 text-gray-500" /> */}
          <p className="text-xs text-muted-foreground">Monitored Prompt</p>
        </div>
        <div className="bg-muted px-3 py-2 rounded-lg border  inline-block">
          <p className="text-sm font-medium">I need a hotel in Los Angeles for next week</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Rank</th> */}
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Bot className="size-3" />
                    Model
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Cpu className="size-3" />
                    Version
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-[10px] min-w-[140px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Swords className="size-3" />
                    Top Competitor
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <AtSign className="size-3" />
                    Mentions
                  </div>
                </th>

                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <EyeIcon className="size-3" />
                    Visibility
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {modelData.map((item) => (
                <tr key={item.rank} className="hover:bg-gray-50">
                  {/* <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">{item.rank}</td> */}
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                    <div className="flex items-center gap-4">
                      {React.createElement(item.icon, {
                        size: 15,
                        type: "color",
                      })}
                      {item.model}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{item.modelVersion}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <Image
                        src={item.topCompetitorIcon}
                        alt={item.topCompetitorName}
                        width={18}
                        height={18}
                        className="rounded-sm border border-gray-200"
                      />
                      {item.topCompetitorName}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{item.score}</td>

                  <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <DonutChart percentage={item.percentage} trend={item.trend as "positive" | "negative" | "flat"} size={18} />
                      <span
                        className={
                          item.trend === "positive" ? "text-emerald-600" : item.trend === "negative" ? "text-red-600" : "text-gray-600"
                        }
                      >
                        {item.trend === "positive" ? (
                          <svg className="inline size-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M7 14l5-5l5 5H7z" />
                          </svg>
                        ) : item.trend === "negative" ? (
                          <svg className="inline size-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M7 10l5 5l5-5H7z" />
                          </svg>
                        ) : (
                          <svg className="inline size-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M5 12h14" />
                          </svg>
                        )}
                        {item.cost.replace("↑", "").replace("↓", "")}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PromptMonitoring() {
  return (
    <div className="flex-1 p-6 bg-white">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Image src="/hubspot.png" alt="HubSpot logo" width={25} height={25} className="rounded-md border border-gray-200" />
          <p className="text-lg font-semibold text-gray-800">HubSpot Organic Search</p>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Gain insight in how your company ranks for industry relevant prompts</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Search className="size-3" />
                    prompt
                  </div>
                </th>
                <th className="px-3 py-2 text-left   min-w-[140px] text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Swords className="size-3" />
                    Top Competitor
                  </div>
                </th>
                <th className="px-3 py-2 text-left  min-w-[140px] text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Bot className="size-3" />
                    top model
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <AtSign className="size-3" />
                    Mentions
                  </div>
                </th>

                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="size-3" />
                    trend
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[
                {
                  prompt: "what is the best crm?",
                  volume: "1,247",
                  model: "ChatGPT",
                  topCompetitorName: "Salesforce",
                  topCompetitorIcon: "/salesforce.jpeg",
                  icon: OpenAI,
                  rate: "94%",
                  trend: "↑ 12%",
                },
                {
                  prompt: "crm for small businesses",
                  volume: "983",
                  model: "Claude",
                  topCompetitorName: "Salesforce",
                  topCompetitorIcon: "/salesforce.jpeg",
                  icon: Claude,
                  rate: "88%",
                  trend: "↑ 5%",
                },
                {
                  prompt: "hubspot vs salesforce",
                  volume: "842",
                  model: "Perplexity",
                  topCompetitorName: "Close.com",
                  topCompetitorIcon: "/close.jpeg",
                  icon: Perplexity,
                  rate: "95%",
                  trend: "↑ 22%",
                },
                {
                  prompt: "free crm software",
                  volume: "689",
                  model: "Gemini",
                  topCompetitorName: "Pipedrive",
                  topCompetitorIcon: "/pipedrive.jpeg",
                  icon: Gemini,
                  rate: "81%",
                  trend: "↑ 8%",
                },
                {
                  prompt: "best marketing automation tools",
                  volume: "542",
                  model: "Claude",
                  topCompetitorName: "Close.com",
                  topCompetitorIcon: "/close.jpeg",
                  icon: Claude,
                  rate: "76%",
                  trend: "↑ 4%",
                },
                {
                  prompt: "crm with email marketing",
                  volume: "423",
                  model: "ChatGPT",
                  topCompetitorName: "Mailchimp",
                  topCompetitorIcon: "/mailchimp.jpeg",
                  icon: OpenAI,
                  rate: "85%",
                  trend: "↓ 2%",
                },
                {
                  prompt: "affordable crm for startups",
                  volume: "387",
                  model: "Deepseek",
                  topCompetitorName: "Pipedrive",
                  topCompetitorIcon: "/pipedrive.jpeg",
                  icon: DeepSeek,
                  rate: "78%",
                  trend: "↑ 15%",
                },
                {
                  prompt: "crm integration with gmail",
                  volume: "356",
                  model: "Gemini",
                  topCompetitorName: "Salesforce",
                  topCompetitorIcon: "/salesforce.jpeg",
                  icon: Gemini,
                  rate: "82%",
                  trend: "↑ 9%",
                },
                // { prompt: "best crm for real estate", volume: "312", model: "Claude", icon: Claude, rate: "91%", trend: "↑ 18%" },
                {
                  prompt: "hubspot vs zoho crm",
                  volume: "298",
                  model: "ChatGPT",
                  topCompetitorName: "Hubspot",
                  topCompetitorIcon: "/hubspot.png",
                  icon: OpenAI,
                  rate: "89%",
                  trend: "↑ 7%",
                },
                {
                  prompt: "crm with ai capabilities",
                  volume: "276",
                  model: "Perplexity",
                  topCompetitorName: "Hubspot",
                  topCompetitorIcon: "/hubspot.png",
                  icon: Perplexity,
                  rate: "93%",
                  trend: "↑ 25%",
                },
                {
                  prompt: "enterprise crm solutions",
                  volume: "245",
                  model: "Grok",
                  topCompetitorName: "Salesforce",
                  topCompetitorIcon: "/salesforce.jpeg",
                  icon: Grok,
                  rate: "79%",
                  trend: "↓ 3%",
                },
              ].map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{item.prompt}</td>

                  <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <Image
                        src={item.topCompetitorIcon}
                        alt={item.topCompetitorName}
                        width={20}
                        height={20}
                        className="rounded-sm border border-gray-200"
                      />
                      {item.topCompetitorName}
                    </div>
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      {React.createElement(item.icon, {
                        size: 15,
                        type: "color",
                      })}
                      {item.model}
                    </div>
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{item.volume}</td>

                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                    <span className={item.trend.includes("↑") ? "text-emerald-600" : "text-red-600"}>
                      {item.trend.includes("↑") ? (
                        <svg className="inline size-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M7 14l5-5l5 5H7z" />
                        </svg>
                      ) : (
                        <svg className="inline size-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M7 10l5 5l5-5H7z" />
                        </svg>
                      )}
                      {item.trend.replace("↑", "").replace("↓", "")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const AIOverviewSection = () => {
  return (
    <div className="flex-1 p-6 bg-white">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Image src="/hbo.jpeg" alt="HBO logo" width={25} height={25} className="rounded-md border border-gray-200" />
          <p className="text-lg font-semibold text-gray-800">HBO Max AI Overviews </p>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Showing how your brand appears in AI responses</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full lg:w-2/3">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-gray-100 rounded-full p-1">
              <Gemini.Color className="size-4" />
            </div>
            <span className="text-xs font-medium">Google AI Overview Response</span>
          </div>

          <div className="mb-4">
            <div className="bg-muted px-3 py-2 rounded-lg border  inline-block">
              <p className="text-sm font-medium">what are the best tv shows to watch now?</p>
            </div>
          </div>

          <div className="text-xs space-y-2 text-muted-foreground border rounded-lg p-3">
            <p>Here are some of the best TV shows of 2025 so far:</p>

            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="font-medium">Shogun</span> (FX/Hulu) - Epic historical drama set in feudal Japan
              </li>
              <li>
                <span className="font-medium text-blue-600">The Penguin</span> (<span className="bg-yellow-100 px-1">HBO</span>) - Crime
                drama spin-off from the Batman universe
              </li>

              <li>
                <span className="font-medium">True Detective: Night Country</span> (<span className="bg-yellow-100 px-1">HBO</span>) -
                Latest installment in the anthology series
              </li>
              <li>
                <span className="font-medium text-blue-600">House of the Dragon</span> (<span className="bg-yellow-100 px-1">HBO</span>) -
                Season 2 of the Game of Thrones prequel
              </li>
              <li>
                <span className="font-medium">Severance</span> (Apple TV+) - Season 2 of the psychological thriller
              </li>
              <li>
                <span className="font-medium">The Bear</span> (FX/Hulu) - Season 3 of the acclaimed restaurant drama
              </li>
            </ul>

            <div className="hidden md:block">
              <p className="mt-2">Additionally, upcoming shows generating buzz:</p>

              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>
                  <span className="font-medium">Squid Game</span> (Netflix) - Long-awaited second season
                </li>
                <li>
                  <span className="font-medium text-blue-600">Dune: Prophecy</span> (<span className="bg-yellow-100 px-1">HBO</span>) -
                  Prequel series to the Dune universe
                </li>
              </ul>
            </div>

            <p>Streaming platforms with the most critically acclaimed shows this year:</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {/* <span className="bg-yellow-100 px-2 py-1 rounded text-xs font-medium">source links</span> */}
              <span className="bg-gray-100 px-2 py-1 rounded text-xs flex items-center">
                <Image src="/netflix.jpeg" alt="Netflix" width={16} height={16} className="mr-1 rounded-xs" />
                netflix.com
              </span>
              <span className="bg-gray-100 px-2 py-1 rounded text-xs flex items-center">
                <Image src="/apple.png" alt="Apple TV+" width={16} height={16} className="mr-1 rounded-xs" />
                apple.com
              </span>
              <span className="hidden md:flex bg-gray-100 px-2 py-1 rounded text-xs items-center">
                <Image src="/hulu.jpeg" alt="Hulu" width={16} height={16} className="mr-1 rounded-xs" />
                hulu.com
              </span>
            </div>
          </div>
        </div>

        <div className="w-full hidden lg:block lg:w-1/3">
          <div className="flex items-center gap-2 mb-2 mt-16">
            <p className="text-xs font-medium">Competitor Mentions</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
            <div className="border rounded-md p-2">
              <div className="flex justify-between items-center">
                <span className="text-xs flex items-center">
                  <Image src="/netflix.jpeg" alt="Netflix" width={20} height={20} className="rounded-sm border border-gray-200 mr-1" />
                  Netflix
                </span>
                <span className="text-xs text-muted-foreground">1,245 mentions</span>
              </div>
            </div>
            <div className="border rounded-md p-2">
              <div className="flex justify-between items-center">
                <span className="text-xs flex items-center">
                  <Image src="/apple.png" alt="Apple TV+" width={20} height={20} className="rounded-sm border border-gray-200 mr-1" />
                  Apple TV+
                </span>
                <span className="text-xs text-muted-foreground">986 mentions</span>
              </div>
            </div>
            <div className="border rounded-md p-2">
              <div className="flex justify-between items-center">
                <span className="text-xs flex items-center">
                  <Image src="/hbo.jpeg" alt="HBO" width={20} height={20} className="rounded-sm border border-gray-200 mr-1" />
                  HBO
                </span>
                <span className="text-xs text-muted-foreground">1,652 mentions</span>
              </div>
            </div>
            <div className="border rounded-md p-2">
              <div className="flex justify-between items-center">
                <span className="text-xs flex items-center">
                  <Image src="/hulu.jpeg" alt="Hulu" width={20} height={20} className="rounded-sm border border-gray-200 mr-1" />
                  Hulu
                </span>
                <span className="text-xs text-muted-foreground">824 mentions</span>
              </div>
            </div>
            <div className="border rounded-md p-2">
              <div className="flex justify-between items-center">
                <span className="text-xs flex items-center">
                  <Image src="/prime.jpeg" alt="Prime" width={20} height={20} className="rounded-sm border border-gray-200 mr-1" />
                  Prime
                </span>
                <span className="text-xs text-muted-foreground">632 mentions</span>
              </div>
            </div>
            <div className="border rounded-md p-2">
              <div className="flex justify-between items-center">
                <span className="text-xs flex items-center">
                  <Image src="/disney.jpeg" alt="Disney+" width={20} height={20} className="rounded-sm border border-gray-200 mr-1" />
                  Disney+
                </span>
                <span className="text-xs text-muted-foreground">412 mentions</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CitationSection = () => {
  return (
    <div className="flex-1 p-6 bg-white">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Image src="/hubspot.png" alt="Hubspot logo" width={25} height={25} className="rounded-md border border-gray-200" />
          <p className="text-lg font-semibold text-gray-800">Hubspot Citations Tracking</p>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Track which sources LLMs cite when mentioning your brand</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <AtSign className="size-3" />
                    Domain
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">URL</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Bot className="size-3" />
                    Model
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Est. Traffic</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Sparkles className="size-3" />
                    Visibility
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[
                {
                  domain: "monday.com",
                  dr: "79",
                  url: "https://monday.com/blog/project-management/crm-vs-project-management/",
                  model: "Gemini",
                  modelIcon: Gemini,
                  traffic: "1,156",
                  visibility: "High",
                  favicon: "/monday.jpeg",
                },
                {
                  domain: "hubspot.com",
                  dr: "91",
                  url: "https://blog.hubspot.com/service/best-crm-software",
                  model: "ChatGPT",
                  modelIcon: OpenAI,
                  traffic: "2,847",
                  visibility: "High",
                  favicon: "/hubspot.png",
                },
                {
                  domain: "salesforce.com",
                  dr: "87",
                  url: "https://www.salesforce.com/resources/articles/crm/",
                  model: "Claude",
                  modelIcon: Claude,
                  traffic: "1,523",
                  visibility: "High",
                  favicon: "/salesforce.jpeg",
                },
                {
                  domain: "zapier.com",
                  dr: "77",
                  url: "https://zapier.com/blog/best-crm-software/",
                  model: "Gemini",
                  modelIcon: Gemini,
                  traffic: "756,557",
                  visibility: "High",
                  favicon: "/zapier.jpeg",
                },
                {
                  domain: "pipedrive.com",
                  dr: "75",
                  url: "https://www.pipedrive.com/en/blog/crm-for-small-business",
                  model: "Sonar Pro",
                  modelIcon: Perplexity,
                  traffic: "892",
                  visibility: "High",
                  favicon: "/pipedrive.jpeg",
                },
                {
                  domain: "capterra.com",
                  dr: "83",
                  url: "https://www.capterra.com/customer-relationship-management-software/",
                  model: "ChatGPT",
                  modelIcon: OpenAI,
                  traffic: "4,267",
                  visibility: "Medium",
                  favicon: "/capterra.jpeg",
                },
                {
                  domain: "freshworks.com",
                  dr: "71",
                  url: "https://www.freshworks.com/crm/",
                  model: "Claude",
                  modelIcon: Claude,
                  traffic: "634",
                  visibility: "High",
                  favicon: "/freshworks.jpeg",
                },
                {
                  domain: "close.com",
                  dr: "58",
                  url: "https://blog.close.com/crm-features/",
                  model: "Deepseek",
                  modelIcon: DeepSeek,
                  traffic: "234",
                  visibility: "Medium",
                  favicon: "/close.jpeg",
                },

                {
                  domain: "zoho.com",
                  dr: "82",
                  url: "https://www.zoho.com/crm/help/getting-started/",
                  model: "Sonar Pro",
                  modelIcon: Perplexity,
                  traffic: "987",
                  visibility: "Medium",
                  favicon: "/zoho.jpeg",
                },
                {
                  domain: "airtable.com",
                  dr: "76",
                  url: "https://blog.airtable.com/what-is-a-crm/",
                  model: "ChatGPT",
                  modelIcon: OpenAI,
                  traffic: "567",
                  visibility: "High",
                  favicon: "/airtable.jpeg",
                },
              ].map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <Image src={item.favicon} alt={item.domain} width={18} height={18} className="rounded-sm border border-gray-200" />
                      <span className="font-medium">{item.domain}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 max-w-xs">
                    <div className="truncate">
                      <p className="text-blue-600 hover:text-blue-800 text-[11px]">{item.url}</p>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      {React.createElement(item.modelIcon, {
                        size: 15,
                        type: "color",
                      })}
                      {item.model}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{item.traffic}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                    <span
                      className={`inline-flex items-center px-1.5 py-[2px] rounded-full text-[10px] font-medium ${
                        item.visibility === "High"
                          ? "bg-emerald-100 text-emerald-800"
                          : item.visibility === "Medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {item.visibility}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
